/**
 * TimelinePanel — F4 cross-reference (2026-05-06).
 *
 * Pokazuje powiązane events: GUNB pozwolenie ↔ plan postępowań ↔ tender ↔ award.
 * Heuristyczne łączenie po (NIP || investor_name || city). Sortowanie chronologiczne.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { Calendar, Activity, RefreshCw, MapPin, ExternalLink } from 'lucide-react';
import { getTenderTimeline } from '../../lib/api';

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(String(s).split(' ')[0]);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(s).slice(0, 10);
  }
}

function fmtMoney(v) {
  if (!v) return '—';
  return new Intl.NumberFormat('pl-PL').format(v) + ' PLN';
}

function TimelineEvent({ ev, isAnchor, onOpen }) {
  const sourceBadgeColor = {
    bzp: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    ted: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    auction: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    funds: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    gunb: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  }[ev.source] || 'bg-muted text-muted-foreground';

  return (
    <div className="flex gap-3 relative">
      {/* Timeline dot */}
      <div className="flex flex-col items-center shrink-0">
        <div
          className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-base"
          style={{
            borderColor: ev.stage_color,
            background: ev.stage_color + (isAnchor ? '40' : '20'),
            boxShadow: isAnchor ? `0 0 0 3px ${ev.stage_color}40` : 'none',
          }}
        >
          {ev.stage_icon}
        </div>
        <div className="w-0.5 flex-1 bg-muted" />
      </div>

      {/* Card */}
      <Card className={`flex-1 mb-3 ${isAnchor ? 'border-2 border-primary' : ''}`}>
        <CardContent className="pt-3 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={{ background: ev.stage_color + '30', color: ev.stage_color }}
                >
                  {ev.stage_label}
                </span>
                {ev.source && (
                  <Badge variant="outline" className={`text-[10px] ${sourceBadgeColor}`}>
                    {ev.source.toUpperCase()}
                  </Badge>
                )}
                {isAnchor && <Badge className="text-[10px] bg-primary text-primary-foreground">TEN TENDER</Badge>}
                {ev.published_at && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {fmtDate(ev.published_at)}
                  </span>
                )}
              </div>
              <div className="text-sm font-medium line-clamp-2">{ev.title || '—'}</div>
              <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-muted-foreground">
                {ev.city && <span><MapPin className="w-3 h-3 inline mr-0.5" />{ev.city}</span>}
                {ev.estimated_value > 0 && <span>{fmtMoney(ev.estimated_value)}</span>}
                {ev.deadline && <span>termin: {fmtDate(ev.deadline)}</span>}
                {ev.designer && <span>projektant: {ev.designer}</span>}
              </div>
            </div>
            {!isAnchor && ev.opportunity_id && !ev.is_permit && (
              <Button
                size="sm" variant="ghost" className="h-7 px-2 shrink-0"
                onClick={() => onOpen(ev.opportunity_id)}
                title="Otwórz powiązany tender"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function TimelinePanel({ tenderId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getTenderTimeline(tenderId);
      setData(r);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="space-y-2"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>;
  }

  if (!data || !data.anchor) {
    return (
      <Card>
        <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
          <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Brak danych do timeline</p>
        </CardContent>
      </Card>
    );
  }

  // Build full chronological list: include anchor in proper temporal position
  const allEvents = [
    ...data.related,
    { ...data.anchor, is_anchor: true },
  ];
  allEvents.sort((a, b) => {
    const da = new Date(String(a.published_at || '').split(' ')[0]);
    const db = new Date(String(b.published_at || '').split(' ')[0]);
    return da - db;
  });

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                Historia inwestycji — timeline
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {data.related_count} powiązanych eventów ·
                {' '}strategia łączenia: <span className="text-foreground/80">{data.match_strategy || '—'}</span>
                {data.investor && <> · inwestor: <span className="text-foreground/80">{data.investor.slice(0, 80)}</span></>}
              </p>
            </div>
            <Button onClick={load} variant="outline" size="sm">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Odśwież
            </Button>
          </div>
        </CardHeader>
      </Card>

      {data.related_count === 0 ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground text-sm">
            Brak powiązanych eventów. System nie znalazł innych pozwoleń GUNB ani innych
            ogłoszeń BZP/TED dla tego inwestora/lokalizacji.
          </CardContent>
        </Card>
      ) : (
        <div className="pt-2">
          {allEvents.map((ev, i) => (
            <TimelineEvent
              key={(ev.opportunity_id || '') + i}
              ev={ev}
              isAnchor={!!ev.is_anchor}
              onOpen={(id) => navigate(`/tenders/${encodeURIComponent(id)}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
